import {
  PageConfig,
  PageConfigSchemaVersion,
} from "../../config/pageConfig.types";
import { DefaultFormId } from "../../config/resolveView";
import { fullWidth, halfWidth } from "../../blocks/layout/columnSpans";
import { enumToOptions } from "../../blocks/fields/enumToOptions";
import {
  ABTestMetric,
  ABTestWinnerType,
  SelectedStartType,
} from "../../config/enums";

export const abtestMetricOptions = enumToOptions(ABTestMetric);
export const abtestWinnerTypeOptions = enumToOptions(ABTestWinnerType);
export const abtestStartTypeOptions = enumToOptions(SelectedStartType);

/**
 * A/B test as page config. Details, Audience and Publish are the dock's tabs; a
 * published test is read-only, so each section is drawn as text then.
 */
export const abtestPageConfig: PageConfig = {
  schemaVersion: PageConfigSchemaVersion,
  entityName: "g4c_abtest",
  viewId: DefaultFormId,
  pageType: "record",
  rows: [
    {
      id: "row-main",
      columns: [
        {
          id: "column-primary",
          span: { desktop: 9, tablet: 9, mobile: 12 },
          blocks: [
            {
              kind: "section",
              id: "section-statistics",
              title: "Statistics",
              visibleWhen: "abtestPublished",
              fields: [
                {
                  id: "field-abtest-statistics",
                  type: "component",
                  name: "abtest_statistics",
                  label: "",
                  componentKey: "abtestStatistics",
                  excludeFromSave: true,
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-details",
              title: "Details",
              wizardStep: true,
              fields: [
                {
                  id: "field-g4c_name",
                  type: "text",
                  name: "g4c_name",
                  label: "Test Name",
                  required: true,
                  isNameField: true,
                  span: fullWidth,
                },
                {
                  id: "field-g4c_description",
                  type: "longtext",
                  name: "g4c_description",
                  label: "Test Description",
                  required: true,
                  rows: 3,
                  span: fullWidth,
                },
                {
                  id: "field-g4c_communicationaid",
                  type: "lookup",
                  name: "g4c_communicationaid",
                  label: "Communication A",
                  required: true,
                  lookupEntity: "g4c_communication",
                  searchFilter: "abtest",
                  searchable: true,
                  optionFilterKey: "abtestCommunicationA",
                  validatorKey: "abtestRequiredLookup",
                  span: halfWidth,
                },
                {
                  id: "field-g4c_communicationbid",
                  type: "lookup",
                  name: "g4c_communicationbid",
                  label: "Communication B",
                  required: true,
                  lookupEntity: "g4c_communication",
                  searchFilter: "abtest",
                  searchable: true,
                  optionFilterKey: "abtestCommunicationB",
                  validatorKey: "abtestRequiredLookup",
                  span: halfWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-audience",
              title: "Audience",
              wizardStep: true,
              fields: [
                {
                  id: "field-g4c_audienceid",
                  type: "lookup",
                  name: "g4c_audienceid",
                  label: "Audience",
                  required: true,
                  lookupEntity: "g4c_communicationaudience",
                  searchable: true,
                  validatorKey: "abtestRequiredLookup",
                  link: { to: "/audience", idKey: "g4c_audienceid" },
                  span: fullWidth,
                },
                {
                  id: "field-audience-summary",
                  type: "component",
                  name: "audience_summary",
                  label: "",
                  componentKey: "abtestAudienceSummary",
                  excludeFromSave: true,
                  visibleWhen: "abtestAudienceSummary",
                  span: fullWidth,
                },
              ],
            },
          ],
        },
        {
          id: "column-sidebar",
          span: { desktop: 3, tablet: 3, mobile: 12 },
          blocks: [
            {
              kind: "section",
              id: "section-test-settings",
              title: "Test Settings",
              wizardStep: true,
              fields: [
                {
                  id: "content-test-settings",
                  kind: "content",
                  type: "text",
                  content:
                    "The test sends Communication A and Communication B to part of the audience for the test period. The winner is then sent to everyone else.",
                  span: fullWidth,
                },
                {
                  id: "field-g4c_testpercentage",
                  type: "number",
                  name: "g4c_testpercentage",
                  label: "Test Percentage",
                  required: true,
                  validatorKey: "abtestTestPercentage",
                  helpText:
                    "The share of the audience that takes part in the test, split evenly between Communication A and B. The rest receive the winner, so some of the audience must be left untested.",
                  span: halfWidth,
                },
                {
                  id: "field-g4c_testperiod",
                  type: "number",
                  name: "g4c_testperiod",
                  label: "Test Period (Hours)",
                  required: true,
                  validatorKey: "abtestTestPeriod",
                  helpText:
                    "How long the test runs before a winner is chosen, in hours (at least 24). It starts when the test starts and must finish before the End Date.",
                  span: halfWidth,
                },
                {
                  id: "field-g4c_metric",
                  type: "picklist",
                  name: "g4c_metric",
                  label: "Metric",
                  required: true,
                  options: abtestMetricOptions,
                  helpText: "What action should the winner be determined by?",
                  span: halfWidth,
                },
                {
                  id: "field-g4c_winnertype",
                  type: "picklist",
                  name: "g4c_winnertype",
                  label: "Winner Type",
                  required: true,
                  options: abtestWinnerTypeOptions,
                  helpText: "How should the winner be confirmed?",
                  span: halfWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-publish",
              title: "Publish",
              wizardStep: true,
              fields: [
                {
                  id: "field-g4c_starttype",
                  type: "picklist",
                  name: "g4c_starttype",
                  label: "When",
                  required: true,
                  options: abtestStartTypeOptions,
                  span: fullWidth,
                },
                {
                  id: "field-g4c_startdate",
                  type: "datetime",
                  name: "g4c_startdate",
                  label: "Start Date",
                  required: true,
                  visibleWhen: "abtestStartDate",
                  span: fullWidth,
                },
                {
                  id: "field-g4c_enddate",
                  type: "datetime",
                  name: "g4c_enddate",
                  label: "End Date",
                  required: true,
                  validatorKey: "abtestEndDate",
                  minDateKey: "abtestEndDate",
                  span: fullWidth,
                },
                {
                  id: "field-g4c_expiryurl",
                  type: "text",
                  name: "g4c_expiryurl",
                  label: "Expiry URL",
                  helpText:
                    "Any attempt to access the email after the end date will redirect to the Expiry URL",
                  span: fullWidth,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
